import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Abril2025_admin from './pressrelease/Admin/Abril2025_admin';
import Abril2025_reportes from './pressrelease/Admin/reportes/Abril2025_reportes';
// Abril 2025
import Abril2025 from './pressrelease/InvuPOS3/Abril2025';

// 1️⃣ Nueva estructura de datos
const releaseNotesData = {
  Products: {
    'Invu POS 3': {
      '2025': [
        { id: 'abril', month:'Abril', label: 'Abril - Actualizaciones de Producto', tags: ['Mejora', 'Nueva Función']},
      ],
    },
    'ADMIN': {
      '2025': [
        { id: 'abril-admin', month:'Abril', label: 'Abril - Actualizaciones de Admin', tags: ['Corrección'], area: 'General' },
        { id: 'abril-admin-reportes', month:'Abril', label: 'Abril - Nuevos reportes agregados', tags: ['Nueva Función'], area: 'Reportes' },
      ],
    },
  },
};

// Meta de tags
const tagMeta = {
  Mejora: 'Mejora/Implementación',
  ['Nueva Función']: 'Nueva Función',
};

export default function Release_notes_page() {
  const location = useLocation();
  const initialProduct = location.state?.product;
  const initialSection = location.state?.section;
  
  // Detectar viewport
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 768
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Leer producto seleccionado desde sessionStorage (o fallback)
  const [selectedProduct, setSelectedProduct] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('selectedProduct') || 'Invu POS 3';
    }
    return 'Invu POS 3';
  });

  // Mantener sidebar abierto en desktop
  const [productOpen, setProductOpen] = useState(isDesktop);
  useEffect(() => {
    setProductOpen(isDesktop);
  }, [isDesktop]);

  // Datos del producto activo
  const productData = releaseNotesData.Products[selectedProduct];
  const years = Object.keys(productData);

  // Año y sección inicial (primer año y primera nota)
  const defaultYear = years[0];
  const [openYear, setOpenYear] = useState(defaultYear);
  useEffect(() => {
    setOpenYear(defaultYear);
  }, [selectedProduct]);

  const defaultSection = productData[defaultYear][0].id;
  const [currentSection, setCurrentSection] = useState(defaultSection);

  // Búsqueda y filtro
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [tagFilter, setTagFilter] = useState('');

  // Aplanar notas para búsqueda
  const products = Object.keys(releaseNotesData.Products);
  const allNotes = useMemo(
    () =>
      products.flatMap(product =>
        Object.entries(releaseNotesData.Products[product]).flatMap(
          ([year, notes]) => notes.map(note => ({ ...note, year, product }))
        )
      ),
    [products]
  );
  const tagsList = Object.keys(tagMeta);

  const filteredByText = useMemo(() => {
    if (!query) return [];
    const term = query.toLowerCase();
    return allNotes.filter(({ label, year, product }) =>
      label.toLowerCase().includes(term) ||
      year.includes(term) ||
      product.toLowerCase().includes(term)
    );
  }, [query, allNotes]);

  const filteredByTag = useMemo(() => {
    if (!tagFilter) return [];
    return allNotes.filter(n => n.tags.includes(tagFilter));
  }, [tagFilter, allNotes]);

  // Handlers
  const handleSelectNote = ({ id, year, product }) => {
    // Guardar selección
    sessionStorage.setItem('selectedProduct', product);
    setSelectedProduct(product);

    setSearchOpen(false);
    setQuery('');
    setTagFilter('');
    setOpenYear(year);
    setCurrentSection(id);
    setProductOpen(true);
  };
  const handleSidebarSelect = id => {
    setTagFilter('');
    setCurrentSection(id);
    setProductOpen(true);
  };

  // Obtener nota seleccionada para ADMIN
  const selectedNote =
    selectedProduct === 'ADMIN'
      ? releaseNotesData.Products.ADMIN['2025'].find(n => n.id === currentSection)
      : null;

  return (
    <>
    {/* Hero: búsqueda + tags */}
    <section className="relative">
        <img className="absolute inset-0 object-cover w-full h-full opacity-85" src="https://images.pexels.com/photos/6612709/pexels-photo-6612709.jpeg" alt="Fondo" />
        <div className="absolute inset-0 bg-gray-900 bg-opacity-30" />
        <div className="relative z-10 max-w-xl mx-auto px-4 py-20 text-center">
          <h1 className="text-white text-4xl font-semibold mb-6">Hola. ¿Cómo podemos ayudarte?</h1>
          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Buscar notas de lanzamiento por mes, año o producto"
              value={query}
              onChange={e => { setQuery(e.target.value); setSearchOpen(true); }}
              onFocus={() => query && setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
              className="w-full bg-white rounded-full px-4 py-3 shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-600"
            />
            {searchOpen && filteredByText.length > 0 && (
              <ul className="absolute left-0 right-0 mt-1 bg-white rounded-lg max-h-60 overflow-auto shadow-lg">
                {filteredByText.map(item => (
                  <li key={`${item.product}-${item.id}-${item.year}`} onMouseDown={() => handleSelectNote(item)} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    {item.product} — {item.label} — {item.year}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {tagsList.map(tag => (
              <button key={tag} onClick={() => { setTagFilter(tag); setSearchOpen(false); setQuery(''); }} className={`px-3 py-1 rounded-full border transition ${tagFilter===tag?'bg-indigo-500 text-white font-bold':'bg-transparent text-gray-200'} hover:bg-indigo-600`}>{tagMeta[tag]}</button>
            ))}
          </div>
        </div>
      </section>

      {/* Layout principal */}
      <div className="text-gray-800">
        <section className="w-full px-4 py-20 mx-auto max-w-7xl flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          {/* Sidebar unchanged (incluye agrupación General/Reportes) */}
          <aside className="w-full md:w-64 bg-white border border-gray-200 md:border-r md:border-none md:sticky md:top-0">
            <h2 className="px-6 py-4 text-xl font-bold cursor-pointer flex justify-between items-center" onClick={() => setProductOpen(o => !o)}>
              Productos <span className="md:hidden">{productOpen ? '▾' : '▸'}</span>
            </h2>
            {productOpen && (
              <div>
                {products.map(product => (
                  <div key={product} className="border-t border-gray-200">
                    <h3
                      className="px-6 py-2 font-semibold cursor-pointer flex justify-between items-center"
                      onClick={() => {
                        // Seleccionar producto y abrir sidebar
                        setSelectedProduct(product);
                        setProductOpen(true);
                        // Establecer año y sección por defecto al cambiar de producto
                        const defaultYear = Object.keys(releaseNotesData.Products[product])[0];
                        setOpenYear(defaultYear);
                        const defaultNoteId = releaseNotesData.Products[product][defaultYear][0].id;
                        setCurrentSection(defaultNoteId);
                      } }
                    >
                      {product} <span className="md:hidden">{selectedProduct === product ? '▾' : '▸'}</span>
                    </h3>
                    {selectedProduct === product && (
                      <div>
                        {Object.entries(releaseNotesData.Products[product]).map(([year, notes]) => (
                          <div key={year} className="border-t border-gray-100">
                            <h4
                              className="px-6 py-2 font-medium cursor-pointer flex justify-between items-center"
                              onClick={() => setOpenYear(o => o === year ? '' : year)}
                            >
                              Release notes {year} <span className="md:hidden">{openYear === year ? '▾' : '▸'}</span>
                            </h4>
                            <ul className={`mt-1 ml-6 space-y-1 mb-4 ${openYear === year ? 'block' : 'hidden'} md:block`}>
                              {(selectedProduct=== 'ADMIN'
                                ? ['General','Reportes']
                                : [''])[0]
                                ? ['General','Reportes'].map(area => (
                                    <React.Fragment key={area}>
                                      <li><h5 className="px-2 py-1 font-semibold text-sm text-gray-500">{area}</h5></li>
                                      {notes.filter(n => n.area=== area).map(n => (
                                        <li key={n.id}><button onClick={() => handleSidebarSelect(n.id)}
                                          className={`w-full text-left px-2 py-1 rounded hover:bg-gray-50 ${currentSection===n.id?'bg-gray-100 border-l-4 border-red-600 font-semibold text-gray-900':'text-gray-600'}`}>{n.label}</button></li>
                                      ))}
                                    </React.Fragment>
                                  ))
                                : notes.map(n => (
                                    <li key={n.id}><button onClick={() => handleSidebarSelect(n.id)}
                                      className={`w-full text-left px-2 py-1 rounded hover:bg-gray-50 ${currentSection===n.id?'bg-gray-100 border-l-4 border-red-600 font-semibold text-gray-900':'text-gray-600'}`}>{n.label}</button></li>
                                  ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </aside>

          {/* Contenido principal */}
          <main className="flex-1">
            {tagFilter ? (
              <div className="mb-8">
                {filteredByTag.length>0 ? (
                  <>
                    <h3 className="text-xl font-semibold mb-4">{filteredByTag.length} resultados para “{tagFilter}”</h3>
                    <ul className="grid grid-cols-1 gap-6">
                    {filteredByTag.map(item => (
                      <li
                        key={`${item.product}-${item.year}-${item.id}`}
                      >
                        <article onClick={() => handleSelectNote(item)} className="rounded-[10px] border cursor-pointer border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex justify-end">
                          <strong
                            className="-me-[3px] -mb-[2px] inline-flex items-center gap-1 rounded-ss-xl rounded-ee-xl bg-indigo-600 px-4 py-2 text-white rounded-tr-[10px] rounded-bl-[10px]"
                          >
                            <svg  xmlns="http://www.w3.org/2000/svg"  width={22}  height={22}  viewBox="0 0 22 22"  fill="none"  stroke="currentColor"  strokeWidth={2}  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-rosette-discount-check"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M5 7.2a2.2 2.2 0 0 1 2.2 -2.2h1a2.2 2.2 0 0 0 1.55 -.64l.7 -.7a2.2 2.2 0 0 1 3.12 0l.7 .7c.412 .41 .97 .64 1.55 .64h1a2.2 2.2 0 0 1 2.2 2.2v1c0 .58 .23 1.138 .64 1.55l.7 .7a2.2 2.2 0 0 1 0 3.12l-.7 .7a2.2 2.2 0 0 0 -.64 1.55v1a2.2 2.2 0 0 1 -2.2 2.2h-1a2.2 2.2 0 0 0 -1.55 .64l-.7 .7a2.2 2.2 0 0 1 -3.12 0l-.7 -.7a2.2 2.2 0 0 0 -1.55 -.64h-1a2.2 2.2 0 0 1 -2.2 -2.2v-1a2.2 2.2 0 0 0 -.64 -1.55l-.7 -.7a2.2 2.2 0 0 1 0 -3.12l.7 -.7a2.2 2.2 0 0 0 .64 -1.55v-1" /><path d="M9 12l2 2l4 -4" /></svg>
                            <span className="text-[14px] font-semibold sm:text-md">
                              {item.product}
                            </span>
                          </strong>
                        </div>

                        <div className='px-5 pb-5'>
                          {/* Año de publicación */}
                          <time
                            dateTime={`${item.year}-01-01`}
                            className="block text-sm text-gray-500"
                          >
                           {item.month} {item.year}
                          </time>


                          {/* Título / Label como botón */}
                          <button
                            onClick={() => handleSelectNote(item)}
                            className="w-full text-left mt-2 hover:underline"
                          >
                            <h3 className="mt-0.5 text-xl font-medium text-gray-900">
                              {item.label} · <span className='italic text-sm text-gray-500'>{item.product}</span>
                            </h3>
                          </button>

                          {/* Badges de tags */}
                          <div className="mt-4">
                            {/* Título arriba */}
                            <h4 className="text-[13px] tracking-wide uppercase text-gray-600 mb-3 tracking-[0.05em]">
                              Categorias
                            </h4>

                            {/* Badges apilados en columna */}
                            <div className="flex flex-wrap gap-4">
                              {item.tags.map(tagKey => {
                                // etiqueta amigable
                                const label = tagMeta[tagKey] || tagKey;

                                // clases base para badge
                                let badgeClasses = [
                                  'inline-flex', 'items-center',
                                  'rounded-md', 'px-3', 'py-1.5',
                                  'text-[13px]', 'font-medium',
                                  'ring-1', 'ring-inset',
                                ];

                                // color según tag
                                if (tagKey === 'Mejora') {
                                  badgeClasses.push('bg-green-50', 'text-green-700', 'ring-green-600/20');
                                } else if (tagKey === 'Nueva Función') {
                                  badgeClasses.push('bg-blue-50', 'text-blue-700', 'ring-blue-700/10');
                                } else if (tagKey === 'Corrección') {
                                  badgeClasses.push('bg-red-50', 'text-red-700', 'ring-red-600/10');
                                } else {
                                  badgeClasses.push('bg-gray-50', 'text-gray-600', 'ring-gray-500/10');
                                }

                                return (
                                  <span key={tagKey} className={badgeClasses.join(' ')}>
                                    {label}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                          </div>
                        </article>
                      </li>
                    ))}
                  </ul>
                  </>
                ) : (
                  <div className="bg-teal-100 border-t-4 border-teal-500 rounded-b text-teal-900 px-4 py-3 shadow-md" role="alert"><div className="flex"><div className="py-1"><svg className="fill-current h-6 w-6 text-teal-500 mr-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M2.93 17.07A10 10 0 1 1 17.07 2.93A10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"/></svg></div><div><p className="font-bold">No hay registros disponibles para “{tagFilter}”</p></div></div></div>
                )}
              </div>
            ) : (
              selectedProduct === 'ADMIN' && selectedNote ? (
                <>
                  <h3 className="uppercase tracking-wider text-sm font-bold mb-2">Área: {selectedNote.area}</h3>
                  {currentSection === 'abril-admin' && <Abril2025_admin/>}
                  {currentSection === 'abril-admin-reportes' && <Abril2025_reportes/>}
                </>
              ) : (
                <>
                  {currentSection === 'abril' &&  <Abril2025/> } 
                </>
              )
            )}
          </main>
        </section>
      </div>
    </>
  );
}
